import { apiClient } from '@/shared/api';
import {
  AvgByAssessmentResponse,
  AvgRatingByDateItem,
  ChartRuleResolveParams,
  ChartRuleResolveResponse,
  ChartRuleSignParams,
  ChartRuleSignResponse,
  ChartSchemaResponse,
  ConversationPerDayItem,
  CustomChartDataParams,
  CustomChartDataResponse,
  EvaluationCommentsResponse,
  IndicatorConversationsParams,
  IndicatorConversationsResponse,
  MissellingDataParams,
  MissellingDataResponse,
  OperatorStatsResponse,
  QualityDetailsParams,
  QualityDetailsResponse,
  QualitySummaryParams,
  QualitySummaryResponse,
  QualityHeatmapParams,
  QualityHeatmapResponse,
  QualityConsultantReportParams,
  QualityConsultantReportResponse,
} from './types';

// GET /dashboard/avg-by-assessment - get statistics by assessment criteria
export const fetchAvgByAssessment = async (params?: { limit?: number; offset?: number }) => {
  const { data } = await apiClient.get<AvgByAssessmentResponse>('/dashboard/avg-by-assessment', { params });
  return data;
};

// GET /dashboard/avg-rating - get average rating statistics by date
export const fetchAvgRating = async (params?: { 'date-from'?: string | null; 'date-to'?: string | null }) => {
  const { data } = await apiClient.get<AvgRatingByDateItem[]>('/dashboard/avg-rating', { params });
  return data;
};

// GET /dashboard/conversation-per-day - get conversation statistics per day
export const fetchConversationPerDay = async (params?: { 'date-from'?: string | null; 'date-to'?: string | null }) => {
  const { data } = await apiClient.get<ConversationPerDayItem[]>('/dashboard/conversation-per-day', { params });
  return data;
};

// GET /dashboard/operator - get operator statistics
export const fetchOperatorStats = async (params?: { limit?: number; offset?: number }) => {
  const { data } = await apiClient.get<OperatorStatsResponse>('/dashboard/operator', { params });
  return data;
};

// GET /dashboard/quality-details - get quality dashboard details (SCT)
export const fetchQualityDetails = async (params?: QualityDetailsParams) => {
  const { data } = await apiClient.get<QualityDetailsResponse>('/dashboard/quality-details', { params });
  return data;
};

// GET /dashboard/quality-summary - aggregated quality dashboard data
export const fetchQualitySummary = async (params?: QualitySummaryParams) => {
  const { data } = await apiClient.get<QualitySummaryResponse>('/dashboard/quality-summary', { params });
  return data;
};

// GET /dashboard/quality-heatmap - heatmap matrix
export const fetchQualityHeatmap = async (params?: QualityHeatmapParams) => {
  const { data } = await apiClient.get<QualityHeatmapResponse>('/dashboard/quality-heatmap', { params });
  return data;
};

// GET /dashboard/quality-indicator-conversations — диалоги одного показателя сводки.
// Запрашиваются в момент перехода, а не при загрузке дашборда.
export const fetchIndicatorConversations = async (params: IndicatorConversationsParams) => {
  const { data } = await apiClient.get<IndicatorConversationsResponse>('/dashboard/quality-indicator-conversations', {
    params,
  });
  return data;
};

// GET /dashboard/quality-evaluation-comments — комментарии по блокам одной оценки.
// Список оценок их не несёт: комментарии запрашивает открытая карточка звонка.
export const fetchEvaluationComments = async (params: { id: number }) => {
  const { data } = await apiClient.get<EvaluationCommentsResponse>('/dashboard/quality-evaluation-comments', {
    params,
  });
  return data;
};

// GET /dashboard/quality-consultant-report - consultant mini-report
export const fetchQualityConsultantReport = async (params: QualityConsultantReportParams) => {
  const { data } = await apiClient.get<QualityConsultantReportResponse>('/dashboard/quality-consultant-report', { params });
  return data;
};

// GET /dashboard/misselling-rgsl - misselling dashboard data (RGS Жизнь)
export const fetchMissellingData = async (params?: MissellingDataParams) => {
  const { data } = await apiClient.get<MissellingDataResponse>('/dashboard/misselling-rgsl', {
    params,
    // Сериализуем массивы как повторяющиеся ключи без `[]`,
    // чтобы FastAPI читал alias `refusal-reasons` (а не `refusal-reasons[]`).
    paramsSerializer: { indexes: null },
  });
  return data;
};

// POST /dashboard/custom-chart/resolve — истолкование описания диаграммы в правило отбора.
// Отказ показывается пользователю в форме диаграммы, поэтому всплывающее уведомление гасим.
export const resolveChartRule = async (params: ChartRuleResolveParams) => {
  const { data } = await apiClient.post<ChartRuleResolveResponse>('/dashboard/custom-chart/resolve', params, {
    silentError: true,
  });
  return data;
};

// POST /dashboard/custom-chart/sign — заверение запроса, правленного вручную.
// Отказ показывается рядом с самим запросом в форме диаграммы, поэтому уведомление гасим.
export const signChartRule = async (params: ChartRuleSignParams) => {
  const { data } = await apiClient.post<ChartRuleSignResponse>('/dashboard/custom-chart/sign', params, {
    silentError: true,
  });
  return data;
};

// GET /dashboard/custom-chart/schema — справочник витрины для правки запроса вручную.
export const fetchChartSchema = async () => {
  const { data } = await apiClient.get<ChartSchemaResponse>('/dashboard/custom-chart/schema');
  return data;
};

// POST /dashboard/custom-chart/data — наполнение диаграммы данными по её правилу.
// Отказ показывается в самом блоке диаграммы: смена фильтров повторяет запрос по каждой
// диаграмме дашборда, и всплывающие уведомления дублировали бы одно и то же сообщение.
export const fetchCustomChartData = async (params: CustomChartDataParams) => {
  const { data } = await apiClient.post<CustomChartDataResponse>('/dashboard/custom-chart/data', params, {
    silentError: true,
  });
  return data;
};
